import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { Button, Icons, Spinner } from "@/components/ui";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { previewSource } from "@/lib/frame-preview-source";
import { frameProjectId } from "@/lib/frame-project";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useDatasetColumns } from "./dataset-columns";
import { DatasetColumnsMenu } from "./dataset-columns-menu";
import { DatasetFieldDialogs } from "./dataset-field-dialogs";
import { DatasetGrid } from "./dataset-grid";
import { datasetSummaryFrom } from "./dataset-values";
import { DATASET_PAGE_SIZE, useDatasetPage } from "./use-dataset-page";
import { useDatasetFields } from "./use-dataset-fields";

/** A table's row cap (01 §10); Add row stops there. */
const MAX_ROWS = 50_000;

/**
 * A Data frame opened as its table: the title and the latest notice, then
 * "1 to 50 of 128" with previous and next (only when the rows take more
 * than one page), Undo last change, Columns (show or hide fields, Add
 * field) and Add row, then the grid. People edit cells, add and delete
 * rows (a deleted row's notice offers Undo), and add, rename and delete
 * fields (a change that deletes data only after they confirm its preview).
 * Rows are live: the page reloads when an
 * agent's write raises the frame's revision, so there is no refresh.
 */
export function DatasetFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesDatasetMessages);
  const frameSurface = useFrameSurface(id);
  const summary = datasetSummaryFrom(previewSource(data));
  const title = frameDisplayTitle(data);
  const projectId = frameProjectId(data);
  const table = useDatasetPage({
    projectId,
    frameId: id,
    dataRevision: summary.dataRevision,
    schemaRevision: summary.schemaRevision,
  });
  const { offset, total } = table;
  const full = total >= MAX_ROWS;
  const addRow = () => void table.addRow();
  const columns = useDatasetColumns(table.fields, table.rows);
  const fields = useDatasetFields({
    projectId,
    frameId: id,
    fields: table.fields,
    schemaRevision: table.schemaRevision,
    onChanged: table.fieldsChanged,
    onStale: table.reloadFields,
  });

  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden text-primary",
      )}
      data-testid="dataset-frame"
      {...frameSurface.attributes}
    >
      {/* One inset with the grid's first column; the icons' visible edge,
          not their round hit area, meets it on the right. */}
      <div className="flex shrink-0 items-center gap-1 pb-2 pl-[var(--open-pad-inline,0.75rem)] pr-[calc(var(--open-pad-inline,0.75rem)-0.25rem)] pt-[var(--open-pad-block-start,0.75rem)]">
        <p
          className="min-w-0 truncate text-appkit-body font-medium"
          data-frame-title="card"
          data-testid="dataset-frame-title"
        >
          {title}
        </p>
        <div className="flex min-w-0 flex-1 items-center gap-1 pl-1">
          <p
            className="min-w-0 truncate text-appkit-mini text-muted"
            aria-live="polite"
            data-testid="dataset-status"
          >
            {/* Keyed, so a repeated notice is announced again. */}
            <span key={table.noticeId}>{table.notice}</span>
          </p>
          {table.noticeUndoable && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => void table.undoDelete()}
              data-testid="dataset-undo-delete"
            >
              {t("undoDelete")}
            </Button>
          )}
        </div>
        {total > DATASET_PAGE_SIZE && (
          <>
            <span
              className="shrink-0 px-1 text-appkit-small tabular-nums text-muted"
              data-testid="dataset-range"
            >
              {t("range", {
                from: offset + 1,
                to: Math.min(offset + DATASET_PAGE_SIZE, total),
                total,
              })}
            </span>
            <Button
              variant="ghost"
              size="iconSm"
              aria-label={t("previousRows")}
              title={t("previousRows")}
              disabled={offset === 0}
              onClick={() =>
                table.setOffset(Math.max(0, offset - DATASET_PAGE_SIZE))
              }
              data-testid="dataset-page-prev"
            >
              <Icons.ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="iconSm"
              aria-label={t("nextRows")}
              title={t("nextRows")}
              disabled={offset + DATASET_PAGE_SIZE >= total}
              onClick={() => table.setOffset(offset + DATASET_PAGE_SIZE)}
              data-testid="dataset-page-next"
            >
              <Icons.ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
        {summary.dataRevision > 0 && (
          <Button
            variant="ghost"
            size="iconSm"
            aria-label={t("undo")}
            title={t("undo")}
            onClick={() => void table.undo()}
            data-testid="dataset-undo"
          >
            <Icons.Undo2 className="h-4 w-4" />
          </Button>
        )}
        <DatasetColumnsMenu
          fields={table.fields}
          isShown={columns.isShown}
          canToggle={columns.canToggle}
          onToggle={columns.toggle}
          canAdd={table.status === "ready"}
          onAdd={fields.startAdd}
        />
        <Button
          variant="ghost"
          size="iconSm"
          aria-label={t("addRow")}
          title={full ? t("tableFull") : t("addRow")}
          disabled={full || table.status !== "ready"}
          onClick={addRow}
          data-testid="dataset-add-row"
        >
          <Icons.Plus className="h-4 w-4" />
        </Button>
      </div>
      {/* Pads itself (`--open-pad-*` when opened), so no surface's content
          padding applies; the grid's columns carry the inline inset. */}
      <div
        className={cn(
          "nodrag min-h-0 flex-1 overflow-auto pb-[var(--open-pad-block-end,0.5rem)]",
          selected && "nowheel",
        )}
        data-frame-measure-content="true"
      >
        {table.status === "loading" ? (
          <div
            className="flex h-full items-center justify-center gap-2 text-appkit-small text-muted"
            data-testid="dataset-loading"
          >
            <Spinner className="h-4 w-4" />
            {t("loadingRows")}
          </div>
        ) : table.status === "error" ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-appkit-small text-muted">
            {t("loadFailed")}
            <Button
              variant="ghost"
              size="sm"
              onClick={table.retry}
              data-testid="dataset-retry"
            >
              {t("retry")}
            </Button>
          </div>
        ) : (
          <>
            {/* The header shows with no rows too: its menus change fields. */}
            <DatasetGrid
              title={title}
              fields={columns.shown}
              canDeleteField={table.fields.length > 1}
              rows={table.rows}
              total={total}
              offset={offset}
              conflict={table.conflict}
              onEdit={table.edit}
              onDelete={(rowId) => void table.deleteRow(rowId)}
              fieldActions={{
                rename: fields.startRename,
                remove: fields.deleteField,
                removeOption: fields.removeOption,
              }}
            />
            {total === 0 && (
              <div className="flex flex-col items-center gap-2 py-6 text-appkit-small text-muted">
                {t("noRows")}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={addRow}
                  data-testid="dataset-empty-add-row"
                >
                  {t("addRow")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <DatasetFieldDialogs fields={fields} />
    </div>
  );
}
